import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { ROLE_LABELS } from '@/lib/format';
import { NAV, NAV_GROUPS } from '@/lib/routes';
import type { NavItem } from '@/lib/routes';
import { useSession } from '@/lib/session';
import { useThemePreference } from '@/lib/theme';
import type { ThemePreference } from '@/lib/theme';

import { Avatar } from './Avatar';
import { Button } from './Button';
import { Icon } from './Icon';

/**
 * The frame every signed-in screen sits in.
 *
 * Desktop: a fixed 15rem rail on the left, content to the right of it.
 * Phone: the rail becomes an off-canvas drawer behind a menu button, and the
 * content keeps a 16px gutter (`--gutter`) on both sides. Nothing in here
 * introduces a horizontal scrollbar - wide content scrolls inside its own box.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, actor } = useSession();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Navigating on a phone should close the drawer. Watching the pathname is
  // more reliable than an onClick on every link.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const visible = NAV.filter((item) => (actor ? item.roles.includes(actor.role) : false));

  return (
    <div className="relative min-h-dvh">
      <div className="paper-grain pointer-events-none fixed inset-0 -z-10" aria-hidden="true" />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-4 focus:py-2 focus:shadow-float"
      >
        Skip to content
      </a>

      {/* The rail. Fixed on desktop; a drawer under `lg`. */}
      <Sidebar
        items={visible}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          user ? (
            <AccountPanel
              name={user.name}
              id={user.id}
              secondary={user.title ?? ROLE_LABELS[user.role]}
            />
          ) : null
        }
      />

      {drawerOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-ink/40 lg:hidden"
        />
      ) : null}

      <div className="lg:pl-60">
        <MobileBar onOpenMenu={() => setDrawerOpen(true)} />
        <main
          id="main"
          className="mx-auto w-full max-w-[86rem] px-[var(--gutter)] py-6 sm:px-6 lg:px-8 lg:py-8"
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Sidebar({
  items,
  open,
  onClose,
  footer,
}: {
  items: readonly NavItem[];
  open: boolean;
  onClose: () => void;
  footer: ReactNode;
}) {
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line bg-surface',
        'transition-transform duration-200 ease-out lg:translate-x-0',
        open ? 'translate-x-0 shadow-modal' : '-translate-x-full lg:shadow-none',
      )}
    >
      <div className="flex h-14 items-center justify-between border-b border-line px-4">
        <Wordmark />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="grid size-8 place-items-center rounded-md text-subtle hover:bg-sunken hover:text-ink lg:hidden"
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Main">
        {NAV_GROUPS.map((group) => {
          const groupItems = items.filter((item) => item.group === group);
          if (groupItems.length === 0) return null;
          return (
            <div key={group} className="mb-5 last:mb-0">
              <h2 className="px-2 pb-1.5 font-sans text-nano font-semibold uppercase tracking-[0.16em] text-subtle">
                {group}
              </h2>
              <ul className="flex flex-col gap-0.5">
                {groupItems.map((item) => (
                  <li key={item.to}>
                    {/* `end` keeps /hours from staying active on /hours/review. */}
                    <NavLink
                      to={item.to}
                      end
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-ui transition-colors',
                          isActive
                            ? 'bg-accent-soft font-semibold text-accent-ink'
                            : 'text-muted hover:bg-sunken hover:text-ink',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive ? (
                            <span
                              className="absolute -left-3 h-5 w-[3px] rounded-r-full bg-accent"
                              aria-hidden="true"
                            />
                          ) : null}
                          <Icon name={item.icon} size={16} />
                          {item.label}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-line p-3">{footer}</div>
    </aside>
  );
}

function Wordmark() {
  return (
    <span className="flex items-center gap-2">
      <span
        className="grid size-7 place-items-center rounded-md bg-accent font-display text-body font-bold leading-none text-on-accent"
        aria-hidden="true"
      >
        B
      </span>
      <span className="font-display text-body font-semibold tracking-tight">
        BotGuide
        <span className="text-subtle"> Portal</span>
      </span>
    </span>
  );
}

function MobileBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  return (
    <div className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-paper/85 px-[var(--gutter)] backdrop-blur-md lg:hidden">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Open menu"
        className="grid size-9 place-items-center rounded-md border border-line bg-surface text-ink"
      >
        <Icon name="menu" size={17} />
      </button>
      <Wordmark />
    </div>
  );
}

/**
 * Who you are, what theme you want, and the way out. Sits at the bottom of the
 * rail because that is where it stops competing with the navigation.
 */
function AccountPanel({
  name,
  id,
  secondary,
}: {
  name: string;
  id: string;
  secondary: string;
}) {
  const { signOut } = useSession();
  const [signingOut, setSigningOut] = useState(false);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex min-w-0 items-center gap-2.5 px-1">
        <Avatar name={name} id={id} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-small font-medium">{name}</span>
          <span className="block truncate text-nano text-subtle">{secondary}</span>
        </span>
      </div>

      <ThemeToggle />

      <Button
        variant="ghost"
        size="sm"
        iconLeft="signOut"
        loading={signingOut}
        className="justify-start"
        onClick={() => {
          setSigningOut(true);
          void signOut().finally(() => setSigningOut(false));
        }}
      >
        Sign out
      </Button>
    </div>
  );
}

const THEME_OPTIONS: readonly { value: ThemePreference; icon: 'sun' | 'moon' | 'monitor'; label: string }[] =
  [
    { value: 'light', icon: 'sun', label: 'Light' },
    { value: 'system', icon: 'monitor', label: 'System' },
    { value: 'dark', icon: 'moon', label: 'Dark' },
  ];

function ThemeToggle() {
  const { preference, setPreference } = useThemePreference();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className="flex items-center gap-0.5 rounded-md border border-line bg-sunken p-0.5"
    >
      {THEME_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={preference === option.value}
          title={option.label}
          onClick={() => setPreference(option.value)}
          className={cn(
            'grid h-6 flex-1 place-items-center rounded-sm transition-colors',
            preference === option.value
              ? 'bg-surface text-ink shadow-card'
              : 'text-subtle hover:text-ink',
          )}
        >
          <Icon name={option.icon} size={13} />
          <span className="sr-only">{option.label}</span>
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export interface PageHeaderProps {
  title: ReactNode;
  /** One sentence under the title. Optional, and usually worth writing. */
  description?: ReactNode;
  /** Filters, tabs or a search box. Wraps under the title on a phone. */
  toolbar?: ReactNode;
  /** The primary action, right-aligned on desktop. */
  actions?: ReactNode;
  className?: string;
}

/**
 * The top of every screen. Consistent placement is most of what makes an app
 * feel like one app rather than eight screens.
 */
export function PageHeader({ title, description, toolbar, actions, className }: PageHeaderProps) {
  return (
    <header className={cn('mb-6 flex flex-col gap-4', className)}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-title font-semibold tracking-[-0.02em] sm:text-display">
            {title}
          </h1>
          {description ? (
            <p className="mt-1 max-w-2xl text-ui text-muted">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {toolbar}
    </header>
  );
}
